import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { deserializeURLSearchParams, validateParams } from "@/lib/utils";
import z from "zod";
import { resumeDraftAction } from "@/app/lib/actions/cat/draft-resume";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";
import { useRetailVehicleAssignmentStore } from "@/stores/mulesoft-vehicle-data-Store";
import { useDraftResumeStore } from "@/stores/draft-resume-store";
import { toast } from "sonner";
import type { Draft } from "@/prisma/generated/client";

const draftResumeSchema = z.object({
  draftId: z.preprocess(
    (val) => (val ? Number(val) : undefined),
    z.number().int().positive().optional(),
  ),
});

export function useDraftResume(
  onStepResolved?: (step: number, draft: Draft) => void,
) {
  const searchParams = useSearchParams();
  const resumedRef = useRef<number | null>(null);
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );
  const setMuleSoftVehicleData = useRetailVehicleAssignmentStore(
    (state) => state.setMuleSoftVehicleData,
  );
  const setInconsistencies = useDraftResumeStore(
    (state) => state.setInconsistencies,
  );

  const { draftId } = validateParams(
    draftResumeSchema,
    deserializeURLSearchParams(searchParams.toString()),
  );

  useEffect(() => {
    if (!draftId || resumedRef.current === draftId) {
      return;
    }

    resumedRef.current = draftId;

    void (async () => {
      const result = await resumeDraftAction({ draftId });
      if (!result.success) {
        toast.error(
          "message" in result && result.message
            ? result.message
            : "Impossible de reprendre le brouillon.",
        );
        return;
      }

      if (!result.data) {
        toast.error("Aucune donnée de reprise n'a été retournée.");
        return;
      }

      setCurrentDraft(result.data.draft);
      setMuleSoftVehicleData(result.data.muleSoftVehicleData);
      setInconsistencies(result.data.inconsistencies);
      onStepResolved?.(result.data.stepIndex, result.data.draft);
    })();
  }, [
    draftId,
    onStepResolved,
    setCurrentDraft,
    setInconsistencies,
    setMuleSoftVehicleData,
  ]);
}
